import { Map as MapIcon } from "@mui/icons-material";
import { Fab, LinearProgress, Stack, Tooltip } from "@mui/material";
import { take } from "lodash";
import { useNavigate } from "react-router-dom";
import Actions from "../../components/actions/Actions";
import AddFab from "../../components/actions/AddFab";
import InfiniteScroll from "../../components/infiniteScroll/InfiniteScroll";
import { useLimit } from "../../misc/useLimit";
import { useOpenInNewTab } from "../../misc/useOpenInNewTab";
import { CustomerCard } from "./CustomerCard";
import { CustomerCsv } from "../csv/CustomerCsv";
import type { AdminCustomerListItem } from "../customersApi";
import { useCustomers } from "../useCustomers";
import { filterCustomers } from "./filterCustomers";
import type { CustomerFilters } from "./CustomerSearch";
import CustomerSearch from "./CustomerSearch";
import type { FC } from "react";
import type { UserRole } from "../../auth/auth";
import CustomerCsvImport from "../csv/CustomerCsvImport";

interface Props {
  readonly role: UserRole;
  readonly search: CustomerFilters;
  readonly onSearchChange: (value: Partial<CustomerFilters>) => void;
  readonly onSearchClear: () => void;
}

const CustomersList: FC<Props> = ({ role, search, onSearchChange, onSearchClear }) => {
  const navigate = useNavigate();
  const { openChildInNewTab } = useOpenInNewTab();
  const { limit, loadMore } = useLimit();
  const { customers, importCustomers, importCustomerContactPersons, importCustomerNotes, isLoading } = useCustomers();
  // This page renders under /customers (ADMIN/AGENT only) — see AdminCustomerListItem in customersApi.ts for why
  // that lets us narrow the role-trimmed fields.
  const adminCustomers = customers as AdminCustomerListItem[];

  const filteredCustomers = filterCustomers(adminCustomers, search);
  const isAdmin = role === "ADMIN";
  return (
    <Stack
      spacing={1}
      sx={{
        marginTop: 1,
        height: "100%",
      }}
    >
      <CustomerSearch filters={search} onChange={onSearchChange} onClear={onSearchClear} />
      {isLoading && <LinearProgress />}
      <InfiniteScroll
        dataLength={limit < filteredCustomers.length ? limit : filteredCustomers.length}
        fetchData={loadMore}
        hasMore={limit < filteredCustomers.length}
      >
        {take(filteredCustomers, limit).map((c) => (
          <CustomerCard
            key={c.id}
            value={c}
            role={role}
            onClick={() => navigate(c.id)}
            onContextMenu={() => openChildInNewTab(c.id)}
          />
        ))}
      </InfiniteScroll>
      <Actions alignToBottom>
        <Tooltip title="Mapa klientów">
          <Fab aria-label="Mapa klientów" color="secondary" onClick={() => navigate("map")}>
            <MapIcon />
          </Fab>
        </Tooltip>
        <CustomerCsv />
        {isAdmin && (
          <CustomerCsvImport
            onImportCustomers={importCustomers}
            onImportContactPersons={importCustomerContactPersons}
            onImportNotes={importCustomerNotes}
          />
        )}
        <AddFab label="Dodaj klienta" onClick={() => navigate("new")} />
      </Actions>
    </Stack>
  );
};

export default CustomersList;
